import { approveByMDDeSlopingSchema } from "@features/vessel-call/vessel-call-status/PortServices/flow/schemas/approveByMDDeSlopingSchema";
import { approveByOperationsDeSlopingSchema } from "@features/vessel-call/vessel-call-status/PortServices/flow/schemas/approveByOperationsDeSlopingSchema";
import { closeDeSlopingSchema } from "@features/vessel-call/vessel-call-status/PortServices/flow/schemas/closeDeSlopingSchema";
import { rejectionSchema } from "@features/vessel-call/vessel-call-status/PortServices/flow/schemas/rejectionSchema";
import { startDeSlopingSchema } from "@features/vessel-call/vessel-call-status/PortServices/flow/schemas/startDeSlopingSchema";
import type { PortServiceFlow } from "@features/vessel-call/vessel-call-status/PortServices/flow/waste-disposal/utils";
import { STATUS_KEYS } from "@features/vessel-call/vessel-call-status/PortServices/preview/statusKeys";

const MARINE_DEPARTMENT_NOTIFY_ROLES = ["Marine Operator"];
const DE_SLOPING_CHANGE_STATUS_EXCLUDED_ROLES = ["marine-office", "harbour-master"];

export const DE_SLOPING_FLOW: PortServiceFlow = {
  [STATUS_KEYS.PENDING_APPROVAL]: {
    serviceRequester: "Shipping Agent",
    canApprove: ["MD"],
    canReject: ["MD"],
    cannotChangeStatusRoles: DE_SLOPING_CHANGE_STATUS_EXCLUDED_ROLES,
    nextStatus: STATUS_KEYS.MD_APPROVED,
    nonApproverStatus: STATUS_KEYS.REJECTED,
    defaultNotifyRoles: ["Shipping Agent"],
    defaultNotifyRolesByStatus: {
      [STATUS_KEYS.REJECTED]: ["Shipping Agent"],
    },
    shouldAppendNotifyRoles: true,
    statusesList: {
      [STATUS_KEYS.MD_APPROVED]: {
        label: "Approved by Marine Department",
        badgeType: "success",
        canBeSelected: true,
        actionPermission: "canApprovePortService",
        extraFields: ["externalNotes", "internalNotes"],
      },
      [STATUS_KEYS.REJECTED]: {
        label: "Rejected",
        badgeType: "error",
        canBeSelected: true,
        actionPermission: "canRejectPortService",
        extraFields: ["rejectionReason"],
      },
    },
  },
  [STATUS_KEYS.MD_APPROVED]: {
    serviceRequester: "Shipping Agent",
    canApprove: ["Shipping Agent"],
    canReject: [],
    nextStatus: STATUS_KEYS.STARTED,
    nonApproverStatus: STATUS_KEYS.REJECTED,
    defaultNotifyRoles: [],
    defaultNotifyRolesByStatus: {
      [STATUS_KEYS.REJECTED]: ["Shipping Agent"],
    },
    shouldAppendNotifyRoles: true,
    statusesList: {
      [STATUS_KEYS.STARTED]: {
        label: "Started",
        badgeType: "success",
        canBeSelected: true,
        actionPermission: "canStartPortService",
        extraFields: ["ats", "etc", "ets"],
      },
      [STATUS_KEYS.REJECTED]: {
        label: "Rejected",
        badgeType: "error",
        canBeSelected: true,
        actionPermission: "canRejectPortService",
        extraFields: ["rejectionReason"],
      },
    },
  },
  [STATUS_KEYS.STARTED]: {
    serviceRequester: "Shipping Agent",
    canApprove: ["Shipping Agent"],
    canReject: [],
    nextStatus: STATUS_KEYS.CLOSED,
    nonApproverStatus: STATUS_KEYS.REJECTED,
    defaultNotifyRoles: MARINE_DEPARTMENT_NOTIFY_ROLES,
    canCancel: [
      {
        userRoles: ["marine-operator", "marine-supervisor"],
        permission: "canCancelPortService",
      },
    ],
    statusesList: {
      [STATUS_KEYS.CLOSED]: {
        label: "Closed",
        badgeType: "success",
        canBeSelected: true,
        actionPermission: "canClosePortService",
        extraFields: ["atc", "actualQuantity", "serviceReportAttachment"],
      },
    },
  },
};

export const DE_SLOPING_WITH_OPERATIONS_FLOW: PortServiceFlow = {
  [STATUS_KEYS.PENDING_APPROVAL]: DE_SLOPING_FLOW[STATUS_KEYS.PENDING_APPROVAL],
  [STATUS_KEYS.MD_APPROVED]: {
    serviceRequester: "Shipping Agent",
    canApprove: ["Operations"],
    canReject: ["Operations"],
    nextStatus: STATUS_KEYS.OPERATIONS_APPROVED,
    nonApproverStatus: STATUS_KEYS.REJECTED,
    defaultNotifyRoles: ["Shipping Agent"],
    defaultNotifyRolesByStatus: {
      [STATUS_KEYS.OPERATIONS_APPROVED]: ["Shipping Agent"],
      [STATUS_KEYS.REJECTED]: ["Shipping Agent"],
    },
    shouldAppendNotifyRoles: true,
    statusesList: {
      [STATUS_KEYS.OPERATIONS_APPROVED]: {
        label: "Approved by Operations",
        badgeType: "success",
        canBeSelected: true,
        actionPermission: "canApprovePortService",
        extraFields: ["notes", "attachment"],
      },
      [STATUS_KEYS.REJECTED]: {
        label: "Rejected",
        badgeType: "error",
        canBeSelected: true,
        actionPermission: "canRejectPortService",
        extraFields: ["rejectionReason"],
      },
    },
  },
  [STATUS_KEYS.OPERATIONS_APPROVED]: {
    serviceRequester: "Shipping Agent",
    canApprove: ["Shipping Agent"],
    canReject: [],
    nextStatus: STATUS_KEYS.STARTED,
    nonApproverStatus: STATUS_KEYS.REJECTED,
    defaultNotifyRoles: [],
    defaultNotifyRolesByStatus: {
      [STATUS_KEYS.REJECTED]: ["Shipping Agent"],
    },
    shouldAppendNotifyRoles: true,
    statusesList: {
      [STATUS_KEYS.STARTED]: {
        label: "Started",
        badgeType: "success",
        canBeSelected: true,
        actionPermission: "canStartPortService",
        extraFields: ["ats", "etc", "ets"],
      },
      [STATUS_KEYS.REJECTED]: {
        label: "Rejected",
        badgeType: "error",
        canBeSelected: true,
        actionPermission: "canRejectPortService",
        extraFields: ["rejectionReason"],
      },
    },
  },
  [STATUS_KEYS.STARTED]: {
    serviceRequester: "Shipping Agent",
    canApprove: ["Shipping Agent"],
    canReject: [],
    nextStatus: STATUS_KEYS.CLOSED,
    nonApproverStatus: STATUS_KEYS.REJECTED,
    defaultNotifyRoles: MARINE_DEPARTMENT_NOTIFY_ROLES,
    canCancel: [
      {
        userRoles: ["operations", "terminal", "marine-operator", "marine-supervisor"],
        permission: "canCancelPortService",
      },
    ],
    statusesList: {
      [STATUS_KEYS.CLOSED]: {
        label: "Closed",
        badgeType: "success",
        canBeSelected: true,
        actionPermission: "canClosePortService",
        extraFields: ["atc", "actualQuantity", "serviceReportAttachment"],
      },
    },
  },
};

type DeSlopingDocument = {
  documentUrl: string;
  documentName: string;
  fileType: string;
};

export const validateDeSlopingExtraFields = ({
  newStatusOption,
  notifyOption,
  internalNotes,
  externalNotes,
  rejectionReason,
  notes,
  attachment,
  flowAts,
  flowEtc,
  flowAtc,
  actualQuantity,
  serviceReportAttachment,
}: {
  newStatusOption: string;
  notifyOption: string[];
  internalNotes: string;
  externalNotes: string;
  rejectionReason: string;
  notes: string;
  attachment: DeSlopingDocument;
  flowAts: string | Date | null;
  flowEtc: string | Date | null;
  flowAtc: string | Date | null;
  actualQuantity?: number;
  serviceReportAttachment: DeSlopingDocument;
}) => {
  switch (newStatusOption) {
    case STATUS_KEYS.MD_APPROVED:
      return approveByMDDeSlopingSchema().safeParse({
        internalNotes,
        externalNotes,
        notificationOption: notifyOption,
      });

    case STATUS_KEYS.OPERATIONS_APPROVED:
      return approveByOperationsDeSlopingSchema().safeParse({
        notes,
        attachment,
        notificationOption: notifyOption,
      });

    case STATUS_KEYS.REJECTED:
      return rejectionSchema().safeParse({
        rejectionReason: rejectionReason || "",
        notificationOption: notifyOption,
      });

    case STATUS_KEYS.STARTED:
      return startDeSlopingSchema().safeParse({
        ats: flowAts,
        etc: flowEtc,
        notificationOption: notifyOption,
      });

    case STATUS_KEYS.CLOSED:
      return closeDeSlopingSchema(flowAts).safeParse({
        atc: flowAtc,
        actualQuantity,
        serviceReportAttachment,
        notificationOption: notifyOption,
      });

    default:
      return null;
  }
};
